/**
 * useViewDrag - 視窗拖動功能
 * 處理視窗拖動的位置計算和限制
 */

import { getNavConfig } from './useViewSnap';

/**
 * 座標位置介面
 */
export interface Position {
    x: number;
    y: number;
}

/**
 * 尺寸大小介面
 */
export interface Size {
    width: number;
    height: number;
}

/**
 * Snap 區域類型
 */
export type SnapZone = 'maximize' | 'left' | 'right' | null;

/**
 * Snap 前的配置介面
 */
export interface PreSnapConfig {
    position: Position;
    size: Size;
}

/**
 * 視圖物件介面
 */
export interface View {
    id?: string;
    routeName?: string;
    routeParams?: Record<string, unknown>;
    title?: string;
    position: Position;
    size: Size;
    zIndex?: number;
    isPinned?: boolean;
    isMaximized?: boolean;
    isSnapped?: boolean;
    snapZone?: SnapZone;
    preSnapConfig?: PreSnapConfig | null;
    isCollapsed?: boolean;
    isTransparent?: boolean;
    scrollPosition?: { top: number; left: number };
}

/**
 * 拖動偏移量介面
 */
export interface DragOffset {
    x: number;
    y: number;
}

/**
 * 位置限制選項介面
 */
export interface ConstrainPositionOptions {
    /** 至少可見的寬度，預設 100 */
    minVisibleWidth?: number;
    /** 至少可見的高度，預設 50 */
    minVisibleHeight?: number;
    /** 是否考慮導航列，預設 true */
    respectNav?: boolean;
}

/**
 * 調整大小起始狀態介面
 */
export interface ResizeStartState {
    x: number;
    y: number;
    width: number;
    height: number;
    posX: number;
    posY: number;
}

/**
 * 調整大小選項介面
 */
export interface ResizeOptions {
    /** 最小寬度，預設 300 */
    minWidth?: number;
    /** 最小高度，預設 200 */
    minHeight?: number;
    /** 最大右邊界 */
    maxRight?: number;
    /** 最大下邊界 */
    maxBottom?: number;
}

/**
 * 調整大小結果介面
 */
export interface ResizeResult {
    width: number;
    height: number;
    x: number;
    y: number;
}

/**
 * 調整方向類型
 */
export type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/**
 * 檢查事件目標是否應該被排除（按鈕等互動元素）
 * @param event - 事件物件
 * @returns 是否應該排除
 */
export function shouldExcludeTarget(event: Event): boolean {
    const target = event.target as HTMLElement;

    // 排除 header 中的按鈕和圖標
    if (target.closest('.view-close-btn, .view-transparent-btn, .view-pin-btn, .view-refresh-btn, .focus-field-icon, .header-buttons')) {
        return true;
    }

    // 排除按鈕元素
    if (target.tagName === 'BUTTON' || target.closest('button')) {
        return true;
    }

    return false;
}

/**
 * 處理最大化視窗的拖動還原
 * @param view - 視圖物件
 * @param clientX - 滑鼠/觸控 X 座標
 * @param clientY - 滑鼠/觸控 Y 座標
 * @returns 是否有進行還原
 */
export function handleMaximizedDragRestore(view: View | null, clientX: number, clientY: number): boolean {
    if (!view) return false;

    // 如果是最大化狀態，拖動時先還原視窗
    if (view.isMaximized || view.snapZone === 'maximize') {
        if (view.preSnapConfig) {
            // 還原大小
            view.size = { ...view.preSnapConfig.size };
            // 計算新位置：讓視窗水平置中於滑鼠位置
            const newX = clientX - view.size.width / 2;
            const newY = clientY - 20; // 標題列高度約 40px，偏移一點
            view.position = { x: newX, y: newY };
            view.preSnapConfig = null;
        }
        view.isSnapped = false;
        view.isMaximized = false;
        view.snapZone = null;
        return true;
    }

    return false;
}

/**
 * 處理分割視窗的拖動還原
 * @param view - 視圖物件
 * @returns 是否有進行還原
 */
export function handleSnappedDragRestore(view: View | null): boolean {
    if (!view) return false;

    // 如果是左右分割狀態，開始拖動時解除 snap 狀態
    if (view.isSnapped && (view.snapZone === 'left' || view.snapZone === 'right')) {
        view.isSnapped = false;
        view.snapZone = null;
        view.preSnapConfig = null;
        return true;
    }

    return false;
}

/**
 * 計算拖動起始偏移量
 * @param clientX - 滑鼠/觸控 X 座標
 * @param clientY - 滑鼠/觸控 Y 座標
 * @param position - 視窗位置
 * @returns 偏移量
 */
export function calculateDragOffset(clientX: number, clientY: number, position: Position): DragOffset {
    return {
        x: clientX - position.x,
        y: clientY - position.y
    };
}

/**
 * 計算拖動後的新位置
 * @param clientX - 滑鼠/觸控 X 座標
 * @param clientY - 滑鼠/觸控 Y 座標
 * @param dragOffset - 拖動偏移量
 * @returns 新位置
 */
export function calculateDragPosition(clientX: number, clientY: number, dragOffset: DragOffset): Position {
    return {
        x: clientX - dragOffset.x,
        y: clientY - dragOffset.y
    };
}

/**
 * 限制視窗位置在可視範圍內
 * @param position - 位置
 * @param size - 大小
 * @param options - 選項
 * @returns 限制後的位置
 */
export function constrainPosition(position: Position, size: Size, options: ConstrainPositionOptions = {}): Position {
    const {
        minVisibleWidth = 100,  // 至少可見的寬度
        minVisibleHeight = 50,  // 至少可見的高度
        respectNav = true       // 是否考慮導航列
    } = options;

    const { navTop, isHorizontal: isHorizontalNav } = respectNav ? getNavConfig() : { navTop: 0, isHorizontal: true };

    const minY = isHorizontalNav ? 60 : 0;
    const maxY = window.innerHeight - minVisibleHeight;
    const minX = -(size.width - minVisibleWidth);
    const maxX = window.innerWidth - minVisibleWidth;

    return {
        x: Math.max(minX, Math.min(position.x, maxX)),
        y: Math.max(minY, Math.min(position.y, maxY))
    };
}

/**
 * 計算調整大小的變化量
 * @param direction - 調整方向 ('n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw')
 * @param startState - 起始狀態
 * @param clientX - 當前滑鼠/觸控 X 座標
 * @param clientY - 當前滑鼠/觸控 Y 座標
 * @param options - 選項
 * @returns 新的大小和位置
 */
export function calculateResize(
    direction: ResizeDirection,
    startState: ResizeStartState,
    clientX: number,
    clientY: number,
    options: ResizeOptions = {}
): ResizeResult {
    const {
        minWidth = 300,
        minHeight = 200,
        maxRight = window.innerWidth - 20,
        maxBottom = window.innerHeight - 20
    } = options;

    const deltaX = clientX - startState.x;
    const deltaY = clientY - startState.y;

    let newWidth = startState.width;
    let newHeight = startState.height;
    let newX = startState.posX;
    let newY = startState.posY;

    // 根據方向調整
    if (direction.includes('e')) {
        newWidth = startState.width + deltaX;
    }
    if (direction.includes('w')) {
        newWidth = startState.width - deltaX;
        newX = startState.posX + deltaX;
    }
    if (direction.includes('s')) {
        newHeight = startState.height + deltaY;
    }
    if (direction.includes('n')) {
        newHeight = startState.height - deltaY;
        newY = startState.posY + deltaY;
    }

    // 限制最小尺寸
    if (newWidth < minWidth) {
        if (direction.includes('w')) {
            newX = startState.posX + (startState.width - minWidth);
        }
        newWidth = minWidth;
    }
    if (newHeight < minHeight) {
        if (direction.includes('n')) {
            newY = startState.posY + (startState.height - minHeight);
        }
        newHeight = minHeight;
    }

    // 限制不超出螢幕
    if (newX + newWidth > maxRight) {
        newWidth = maxRight - newX;
    }
    if (newY + newHeight > maxBottom) {
        newHeight = maxBottom - newY;
    }
    if (newX < 0) {
        newWidth += newX;
        newX = 0;
    }
    if (newY < 0) {
        newHeight += newY;
        newY = 0;
    }

    return {
        width: newWidth,
        height: newHeight,
        x: newX,
        y: newY
    };
}

/**
 * 建立調整大小的起始狀態
 * @param clientX - 滑鼠/觸控 X 座標
 * @param clientY - 滑鼠/觸控 Y 座標
 * @param view - 視圖物件
 * @returns 起始狀態
 */
export function createResizeStartState(clientX: number, clientY: number, view: View): ResizeStartState {
    return {
        x: clientX,
        y: clientY,
        width: view.size.width,
        height: view.size.height,
        posX: view.position.x,
        posY: view.position.y
    };
}

/**
 * 檢查視窗是否可以拖動
 * @param view - 視圖物件
 * @returns 是否可以拖動
 */
export function canDragView(view: View | null): boolean {
    if (!view) return false;
    if (view.isPinned) return false;
    return true;
}

/**
 * 檢查視窗是否可以調整大小
 * @param view - 視圖物件
 * @returns 是否可以調整大小
 */
export function canResizeView(view: View | null): boolean {
    if (!view) return false;
    if (view.isPinned) return false;
    if (view.isMaximized) return false;
    if (view.snapZone === 'maximize') return false;
    return true;
}

export default {
    shouldExcludeTarget,
    handleMaximizedDragRestore,
    handleSnappedDragRestore,
    calculateDragOffset,
    calculateDragPosition,
    constrainPosition,
    calculateResize,
    createResizeStartState,
    canDragView,
    canResizeView
};
